import { all, AllEffect, ForkEffect, put, PutEffect, select, SelectEffect, takeLatest } from 'redux-saga/effects';
import {
  ANALYZE_CHECKOUT_STEP_VIEW,
  ANALYZE_ORDER_CONFIRMATION_PAGE,
  AnalyzeCheckoutStepViewAction,
  AnalyzeOrderConfirmationPageAction,
  CHECKOUT_TYPE,
} from 'b2b-apps/shop/analytics/redux';
import { analyzeView } from 'b2b-apps/shop/analytics/redux/tealium/actionCreators';
import { ANALYTICS_BOOLEAN, TEALIUM_INTERACTION_ID, TEALIUM_PAGE_TYPE } from 'b2b-apps/shop/analytics/types/types';
import {
  prepareDeliveryDestination,
  preparePrice,
  prepareQuantity,
  prepareStock,
} from 'b2b-apps/shop/analytics/utils/analytics';
import { getCheckout } from 'b2b-apps/shop/checkout/redux/checkout/selectors';
import { getPlaceOrder } from 'b2b-apps/shop/checkout/redux/order/selectors';
import { getPlaceCollectedOrder } from 'b2b-apps/shop/collectedOrder/redux/selectors';
import { RemoteRecord } from 'b2b-apps/shop/common/redux/RemoteRecord';
import { CartProductType } from 'b2b-common/core/cart/api/CartTypes';
import { CheckoutDataType } from 'b2b-common/core/checkout/Checkout.types';


function* analyzeCheckoutStepViewSaga(
  action: AnalyzeCheckoutStepViewAction,
): Generator<PutEffect | SelectEffect, void, RemoteRecord<CheckoutDataType>> {
  const checkoutState: RemoteRecord<CheckoutDataType> = yield select(getCheckout);

  const products = checkoutState?.data?.products || [];
  const shipping = checkoutState?.data?.shipping;
  const payment = checkoutState?.data?.payment;

  yield put(analyzeView({
    page: {
      type: TEALIUM_PAGE_TYPE.checkout,
    },
    interaction: TEALIUM_INTERACTION_ID.checkout,
    checkout: {
      deliverydest: prepareDeliveryDestination(shipping.selected),
      type: CHECKOUT_TYPE.login,
    },
    order: {
      payment: {
        type: payment.selected || '',
      },
    },
    prod: products.map(p => ({
      action: action.payload.step,
      categories: p.analytics.categories,
      id: p.sku,
      price: preparePrice(p.single.price),
      quan: prepareQuantity(p.cart.quantity),
      stock: prepareStock(p.settings.quantity),
    })),
    virtualPageView: ANALYTICS_BOOLEAN.true,
  }));
}

function* analyzeOrderConfirmationPageSaga(action: AnalyzeOrderConfirmationPageAction):
  Generator<PutEffect | SelectEffect, void, RemoteRecord<CheckoutDataType>> {
  const placeOrderState: RemoteRecord<CheckoutDataType> = action.payload.isCollectedOrder
    ? yield select(getPlaceCollectedOrder) : yield select(getPlaceOrder);

  if (!placeOrderState) {
    return;
  }

  const { data } = placeOrderState;

  yield put(analyzeView({
    page: {
      type: TEALIUM_PAGE_TYPE.orderConfirmation,
    },
    interaction: TEALIUM_INTERACTION_ID.orderConfirmation,
    prod: data?.products?.map((product: CartProductType) => ({
      action: 'purchase',
      id: `${product.sku}`,
      categories: product.analytics.categories,
      price: preparePrice(product.single.price),
      vat: preparePrice(product.single.price - product.single.priceWithoutVat),
      //no discount
      quan: prepareQuantity(product.cart.quantity),
      stock: prepareStock(product.settings.quantity),
    })),
    order: {
      id: `${data?.token}`,
      revenue: preparePrice(data?.prices?.finalWithoutDiscounts),
      shipping: preparePrice(data?.prices?.shippingCosts),
      smallQuantitySurcharge: preparePrice(data?.prices?.smallQuantitiesCost),
      payment: {
        cost: preparePrice(data?.prices?.paymentCosts),
        type: `${data?.payment?.selected || ''}`,
      },
    },
    checkout: {
      deliverydest: prepareDeliveryDestination(data?.shipping?.selected),
      type: CHECKOUT_TYPE.login,
    },
  }));
}

function* checkoutAnalyticsSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(ANALYZE_CHECKOUT_STEP_VIEW, analyzeCheckoutStepViewSaga),
    takeLatest(ANALYZE_ORDER_CONFIRMATION_PAGE, analyzeOrderConfirmationPageSaga),
  ]);
}

export { checkoutAnalyticsSaga };
